/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { MatDrawer, MatDrawerContainer, MatDrawerContent } from '@angular/material/sidenav';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { Subject, zip } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { LoggingService, Logger } from 'crimson';
import { AppBarService } from 'app-bar';
import { CommonDynamicRedirect, CommonRedirectShortLinksMap } from '../../../../gfn/shared/src/lib/redirect/redirect';
import { RedirectService } from '../../../../gfn/shared/src/lib/redirect/redirect.service';
import { PlaytestState, PlaytestMenu } from '../shared';
import { PlaytestLiveService } from '../core/services/playtest-live.service';
import { SessionsComponent } from '../sessions/sessions.component';
import { SettingsComponent } from '../settings/settings.component';

@Component({
    selector: 'nv-playtest-home',
    templateUrl: './playtest-home.component.html',
    styleUrls: ['./playtest-home.component.scss'],
    imports: [MatDrawerContainer, MatDrawer, MatButton, MatIcon, MatDrawerContent, SessionsComponent, SettingsComponent]
})
export class PlaytestHomeComponent implements OnInit, OnDestroy {
    private destroy$: Subject<boolean> = new Subject<boolean>();
    private logger: Logger;

    termsConditionsUrl: string;
    accountTermsUrl: string;
    privacyPolicyUrl: string;
    termsConditionsShortUrl = CommonRedirectShortLinksMap[CommonDynamicRedirect.TermsConditions];
    accountTermsShortUrl = CommonRedirectShortLinksMap[CommonDynamicRedirect.AccountTerms];
    privacyPolicyShortUrl = CommonRedirectShortLinksMap[CommonDynamicRedirect.PrivacyPolicy];

    username = '';
    readonly PlaytestMenu = PlaytestMenu;
    menuOption = PlaytestMenu.playtest;

    sidebarButtons = [
        {
            title: 'Playtests',
            action: PlaytestMenu.playtest,
            icon: 'game_controller'
        },
        {
            title: 'Manage invites',
            action: PlaytestMenu.manageInvite,
            icon: 'send'
        }
        //        {
        //            title: 'Settings',
        //            action: PlaytestMenu.settings,
        //            icon: 'settings'
        //        }
    ];

    @ViewChild('sidenav', { static: true }) sidenav: MatDrawer;

    constructor(
        private playtestLiveService: PlaytestLiveService,
        private loggingService: LoggingService,
        private appBarService: AppBarService,
        private redirectService: RedirectService
    ) {
        this.logger = this.loggingService.getLogger('PlaytestHomeComponent');
    }

    ngOnInit(): void {
        this.appBarService.setNavButtonMenu();
        this.appBarService.setLogoRevealed();

        const termCondition$ = this.redirectService.getRedirectUrl(CommonDynamicRedirect.TermsConditions).pipe(take(1));
        const privacyPolicy$ = this.redirectService.getRedirectUrl(CommonDynamicRedirect.PrivacyPolicy).pipe(take(1));
        zip(termCondition$, privacyPolicy$)
            .pipe(takeUntil(this.destroy$))
            .subscribe(([termConditionUrl, privacyPolicyUrl]) => {
                this.termsConditionsUrl = termConditionUrl;
                this.privacyPolicyUrl = privacyPolicyUrl;
            });

        this.playtestLiveService
            .getPageState()
            .pipe(takeUntil(this.destroy$))
            .subscribe(state => {
                if (
                    state === PlaytestState.home ||
                    state === PlaytestState.settings ||
                    state === PlaytestState.invites ||
                    state === PlaytestState.login
                ) {
                    // Clear the user name if it changed
                    const username = this.playtestLiveService.getUserDisplayName();
                    if (username !== this.username && this.username !== '') {
                        this.logger.info('Clearing username');
                        this.appBarService.clearIcons();
                        this.username = '';
                    }

                    // Set the user name if necessary
                    if (username !== '' && this.username === '') {
                        this.logger.info('Set username');
                        this.username = username;
                        this.appBarService.setIcons([
                            {
                                loginData: {
                                    username: this.username,
                                    avatarSvg: 'account_circle'
                                },
                                menu: [
                                    {
                                        id: 'privacy_policy',
                                        item: 'Privacy Policy'
                                    },
                                    {
                                        id: 'legal_info',
                                        item: 'Legal Info'
                                    },
                                    {
                                        id: 'terms_of_use',
                                        item: 'Terms of Use'
                                    },
                                    {
                                        id: 'logout',
                                        item: 'Log out'
                                    }
                                ]
                            }
                        ]);
                    }

                    if (state === PlaytestState.invites) {
                        this.appBarService.setTitle({ entry: 'Manage Invites' });
                    } else if (state === PlaytestState.settings) {
                        this.appBarService.setTitle({ entry: 'Settings' });
                    } else if (state === PlaytestState.home) {
                        this.appBarService.setTitle({ entry: 'GeForce NOW Playtest' });
                    }

                    this.menuOption = this.playtestLiveService.getTestHomeMenuOption();
                }
            });

        this.appBarService.actionClicked$.pipe(takeUntil(this.destroy$)).subscribe((id: string) => {
            if (id === 'menu') {
                this.sidenav.toggle();
            } else if (id === 'privacy_policy') {
                window.open(this.privacyPolicyUrl, '_blank');
            } else if (id === 'legal_info') {
                window.open('http://www.nvidia.com/object/legal_info.html', '_blank');
            } else if (id === 'terms_of_use') {
                window.open(this.termsConditionsUrl, '_blank');
            } else if (id === 'logout') {
                this.playtestLiveService.logout();
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
        this.appBarService.setNavButtonEmpty();
    }

    onClickMenuOption(menuOption: PlaytestMenu) {
        if (menuOption === PlaytestMenu.playtest) {
            this.playtestLiveService.setTestHomeMenuOption(PlaytestMenu.playtest);
            this.playtestLiveService.setCurrentPageState(PlaytestState.home);
        } else if (menuOption === PlaytestMenu.manageInvite) {
            this.playtestLiveService.setTestHomeMenuOption(PlaytestMenu.manageInvite);
            this.playtestLiveService.setCurrentPageState(PlaytestState.invites);
        } else if (menuOption === PlaytestMenu.settings) {
            this.playtestLiveService.setTestHomeMenuOption(PlaytestMenu.settings);
            this.playtestLiveService.setCurrentPageState(PlaytestState.settings);
        }
        this.sidenav.toggle();
    }
}
